<script setup lang="ts">
  import { useCssModule } from 'vue';

  export interface RadioVariantProps {
    /**
     * Whether the input should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * A unique string to distinguish one Radio instance from another
     * and to link the label to the input; required for accessibility
     */
    id: string | number;

    /**
     * The description which appears to the right of the radio button
     */
    label?: string;

    /**
     * value bound to the parent component
     */
    modelValue?: string | undefined;

    /**
     * Passed to the "name" attribute of the `<input>` element.
     */
    name?: string;

    /**
     * The value for the radio input.
     */
    value: string;
  }

  const props = defineProps<RadioVariantProps>();
  const classes = useCssModule();

  const emit = defineEmits<{
    (e: 'input', event: Event): void;
  }>();
</script>

<template>
  <div :class="[classes.root, { 'w-full': props.fullWidth }]">
    <input
      :id="`${props.name}-${props.id}`"
      class="sr-only"
      :class="classes.input"
      type="radio"
      :name="props.name"
      :value="props.value"
      :checked="props.modelValue === props.value"
      :disabled="props.disabled || props.disabled"
      @input="emit('input', $event)"
    />
    <label :class="classes.label" :for="`${props.name}-${props.id}`">
      {{ props.label }}
    </label>
  </div>
</template>

<style module>
  @reference "../../../../styles/main.css";

  .label {
    display: inline-block;
    padding: --spacing(1) --spacing(3);
    border: 1px solid var(--color-ice-500);
    font-weight: var(--stash-font-weight-normal);
    line-height: 1.375rem;
    color: var(--color-ice-900);
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .input:disabled ~ .label {
    background-color: var(--color-ice-100);
    color: var(--color-ice-500);
    cursor: auto;
  }

  .input:checked:not(:disabled) ~ .label {
    border-color: var(--color-blue-500);
    color: var(--color-white);
    background-color: var(--color-blue-500);
    font-weight: var(--stash-font-weight-semibold);
  }

  .input:not(:checked, :disabled) ~ .label:hover {
    border-color: var(--color-blue-500);
  }
</style>
